/**
 * /customers/:id — Customer detail page.
 *
 * Header card · Identity block · Ticket timeline · Channel mix · CSAT history
 * · Internal notes · Edit / Delete actions.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, User, Phone, Mail, Globe, MapPin, Hash, Edit, Trash2,
  Save, MessageSquare, BarChart3, Calendar, AlertTriangle, Smile, GitMerge,
  FilePlus, Building2,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { ticketTypeMeta } from "@/lib/ticketType";
import MergeCustomerDialog from "@/components/customers/MergeCustomerDialog";
import TagEditor from "@/components/customers/TagEditor";
import { useRecentlyViewedCustomers } from "@/lib/useRecentlyViewedCustomers";
import { useTicketCreator } from "@/context/TicketCreatorContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { phoneLengthError, requiredDigits } from "@/lib/phoneFormat";

const PIE_COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ec4899", "#0ea5e9", "#8b5cf6"];

function StatusPill({ value }) {
  const tone = {
    open: "bg-amber-50 text-amber-700 border-amber-200",
    in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
    pending_customer: "bg-sky-50 text-sky-700 border-sky-200",
    on_hold: "bg-slate-100 text-slate-700 border-slate-300",
    resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-600 border-slate-300",
    cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  }[value] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${tone}`}>{value?.replace("_", " ")}</span>;
}

function PrioPill({ value }) {
  const tone = {
    critical: "bg-rose-50 text-rose-700 border-rose-200",
    high: "bg-amber-50 text-amber-700 border-amber-200",
    medium: "bg-slate-50 text-slate-700 border-slate-200",
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[value] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`text-[10px] capitalize border rounded-full px-2 py-0.5 ${tone}`}>{value}</span>;
}

function Field({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className={`text-sm text-slate-800 ${mono ? "font-mono" : ""} break-words`}>{value || <span className="text-slate-400">—</span>}</div>
      </div>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [clients, setClients] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  useEffect(() => {
    api.get("/customer-clients", { params: { active_only: true } }).then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/country-codes").then((r) => setCountryCodes(r.data || [])).catch(() => {});
  }, []);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);

  const findDuplicates = async () => {
    setDupOpen(true);
    setDupLoading(true);
    try {
      const r = await api.get(`/customers/${id}/duplicates`);
      setDuplicates(r.data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to scan");
    } finally {
      setDupLoading(false);
    }
  };

  const { push: pushRecent } = useRecentlyViewedCustomers();
  const { openCreator } = useTicketCreator();

  const load = async () => {
    setLoading(true);
    try {
      const [c, tl] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/timeline`),
      ]);
      setCustomer(c.data);
      setTimeline(tl.data || []);
      setNotes(c.data.notes || "");
      pushRecent(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const openEdit = () => {
    setEditForm({
      name: customer.name || "",
      email: customer.email || "",
      language: customer.language || "English",
      phone: customer.phone || "",
      alt_phone: customer.alt_phone || "",
      country_code: customer.country_code || "+974",
      online_order_no: customer.online_order_no || "",
      store_location: customer.store_location || "",
      notes: customer.notes || "",
      tags: customer.tags || [],
      client_id: customer.client_id || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const phoneErr = phoneLengthError(editForm.country_code, editForm.phone, countryCodes, "Phone");
    if (phoneErr) return toast.error(phoneErr);
    const altErr = editForm.alt_phone
      ? phoneLengthError(editForm.country_code, editForm.alt_phone, countryCodes, "Alt phone")
      : null;
    if (altErr) return toast.error(altErr);
    try {
      await api.patch(`/contacts/${id}`, editForm);
      toast.success("Customer updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/customers/${id}/notes`, { notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingNotes(false);
    }
  };

  const [gdprOpen, setGdprOpen] = useState(false);
  const [gdprConfirm, setGdprConfirm] = useState("");
  const gdprDelete = async () => {
    try {
      const r = await api.delete(`/customers/${id}/gdpr`);
      toast.success(`Deleted · ${r.data.tickets_anonymized} ticket(s) anonymized`);
      navigate("/customers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GDPR delete failed");
    }
  };
  const gdprCanConfirm = gdprConfirm.trim().toUpperCase() === "DELETE";

  if (loading) return <div className="text-sm text-slate-400">Loading customer…</div>;
  if (!customer) return <div className="text-sm text-rose-600">Customer not found.</div>;

  const csatPoints = (customer.csat_history || []).slice().reverse().map((c) => ({
    name: c.code, score: c.csat?.score,
  }));
  const csatAvg = csatPoints.length
    ? Math.round((csatPoints.reduce((s, p) => s + (p.score || 0), 0) / csatPoints.length) * 10) / 10
    : null;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to customers
      </Link>

      {/* Header card */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-heading text-lg font-bold">
                {(customer.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="customer-name">
                  {customer.name || "(no name)"}
                </h1>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                  <span className="font-mono">{customer.phone_e164 || customer.phone}</span>
                  {customer.email && <><span>·</span><span>{customer.email}</span></>}
                  {customer.language && <><span>·</span><span>{customer.language}</span></>}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline" className="bg-slate-50 text-slate-700">
                    {customer.stats?.ticket_count || 0} ticket{customer.stats?.ticket_count === 1 ? "" : "s"}
                  </Badge>
                  {customer.stats?.open_count > 0 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {customer.stats.open_count} open
                    </Badge>
                  )}
                  {csatAvg !== null && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Smile className="w-3 h-3 mr-1" /> CSAT {csatAvg}/5
                    </Badge>
                  )}
                  {(customer.tags || []).map((t) => (
                    <Badge key={t} variant="outline"
                           className={`text-[10px] ${t === "vip" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-indigo-50 text-indigo-700 border-indigo-200"}`}
                           data-testid={`customer-tag-${t}`}>
                      {t}
                    </Badge>
                  ))}
                  {customer.health && (
                    <HealthChip health={customer.health} />
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={findDuplicates} data-testid="customer-find-duplicates-btn">
                <GitMerge className="w-3.5 h-3.5 mr-1.5" /> Find duplicates
              </Button>
              <Button onClick={() => openCreator({ customerId: id })}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="customer-new-ticket-btn">
                <FilePlus className="w-3.5 h-3.5 mr-1.5" /> New ticket
              </Button>
              <Button variant="outline" onClick={openEdit} data-testid="customer-edit-btn">
                <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button variant="outline" onClick={() => setGdprOpen(true)} className="text-rose-600 hover:text-rose-700"
                      data-testid="customer-gdpr-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> GDPR delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sticky notes banner (Phase 5.8j) — surfaces internal notes prominently */}
      {customer.notes && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5"
             data-testid="sticky-notes-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Sticky notes</div>
            <div className="text-sm text-amber-900 mt-0.5 whitespace-pre-wrap">{customer.notes}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Identity */}
        <Card className="border-slate-200 lg:col-span-1">
          <CardHeader>
            <CardTitle className="font-heading text-base">Identity</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100 -mt-2">
            <Field icon={User} label="Name" value={customer.name} />
            <Field icon={Phone} label="Primary phone" value={customer.phone_e164 || customer.phone} mono />
            <Field icon={Phone} label="Alt phone" value={customer.alt_phone} mono />
            <Field icon={Mail} label="Email" value={customer.email} />
            <Field icon={Globe} label="Language" value={customer.language} />
            <Field icon={Hash} label="Online order #" value={customer.online_order_no} mono />
            <Field icon={MapPin} label="Store location" value={customer.store_location} />
            {customer.client_name && <Field icon={Building2} label="Client / Account" value={customer.client_name} />}
            {customer.last_ticket_at && (
              <Field icon={Calendar} label="Last ticket"
                     value={new Date(customer.stats?.last_ticket_at || customer.last_ticket_at).toLocaleString()} />
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Internal notes
            </CardTitle>
            <span className="text-[10px] uppercase tracking-wider text-slate-400">visible to all agents</span>
          </CardHeader>
          <CardContent className="-mt-2 space-y-2">
            <Textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={5}
              data-testid="customer-notes-textarea"
              placeholder="VIP — always email, never call. Has hearing aid. Repeated refund customer — verify order # twice."
              className="text-sm"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={saveNotes} disabled={savingNotes}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="customer-save-notes-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {savingNotes ? "Saving…" : "Save notes"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-600" /> Ticket timeline
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{timeline.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-100" data-testid="customer-timeline">
              {timeline.map((t) => {
                const tm = ticketTypeMeta(t.ticket_type);
                return (
                  <Link to={`/tickets/${t.id}`} key={t.id}
                        className="block px-5 py-3 hover:bg-slate-50 transition-colors"
                        data-testid={`timeline-row-${t.code}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      {tm && <tm.icon className={`w-3.5 h-3.5 ${tm.iconColor}`} strokeWidth={2.2} />}
                      <span className="font-mono text-xs font-semibold text-indigo-600 shrink-0">{t.code}</span>
                      <span className="text-sm text-slate-800 flex-1 truncate">{t.subject}</span>
                      <StatusPill value={t.status} />
                      <PrioPill value={t.priority} />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1 ml-6 flex-wrap">
                      <span>{t.channel}</span>
                      <span>·</span>
                      <span>{t.category}{t.subcategory ? ` → ${t.subcategory}` : ""}</span>
                      <span>·</span>
                      <span>{new Date(t.created_at).toLocaleDateString()}</span>
                      {t.owner_email && <><span>·</span><span>{t.owner_email}</span></>}
                    </div>
                  </Link>
                );
              })}
              {timeline.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-slate-400">No tickets yet for this customer.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {(customer.channels || []).length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-600" /> Channel mix
                </CardTitle>
              </CardHeader>
              <CardContent className="h-52">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={customer.channels} dataKey="count" nameKey="channel"
                         cx="50%" cy="50%" outerRadius={60} innerRadius={32}>
                      {(customer.channels || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {csatPoints.length > 1 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Smile className="w-4 h-4 text-emerald-600" /> CSAT history
                </CardTitle>
              </CardHeader>
              <CardContent className="h-52">
                <ResponsiveContainer>
                  <LineChart data={csatPoints}>
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#64748b" }} />
                    <YAxis domain={[1, 5]} tick={{ fontSize: 10, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Line type="monotone" dataKey="score" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit customer</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Update the customer's profile. Phone changes are checked for uniqueness — Kippu won't let two records share a phone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                       data-testid="edit-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Language</Label>
                <Input value={editForm.language} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Country code</Label>
                <Select value={editForm.country_code} onValueChange={(v) => setEditForm({ ...editForm, country_code: v })}>
                  <SelectTrigger className="h-9" data-testid="edit-country-code-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {countryCodes.map((c) => (
                      <SelectItem key={c.id} value={c.code}>
                        <span className="font-mono text-xs mr-2">{c.code}</span>{c.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Primary phone</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => {
                    const cap = requiredDigits(editForm.country_code, countryCodes) || 15;
                    setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, "").slice(0, cap) });
                  }}
                  placeholder={requiredDigits(editForm.country_code, countryCodes) ? `${requiredDigits(editForm.country_code, countryCodes)} digits` : "Phone"}
                  data-testid="edit-phone" />
                {editForm.phone && phoneLengthError(editForm.country_code, editForm.phone, countryCodes, "Phone") && (
                  <p className="text-[11px] text-rose-600" data-testid="edit-phone-error">{phoneLengthError(editForm.country_code, editForm.phone, countryCodes, "Phone")}</p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Alt phone</Label>
              <Input
                value={editForm.alt_phone}
                onChange={(e) => {
                  const cap = requiredDigits(editForm.country_code, countryCodes) || 15;
                  setEditForm({ ...editForm, alt_phone: e.target.value.replace(/\D/g, "").slice(0, cap) });
                }} />
              {editForm.alt_phone && phoneLengthError(editForm.country_code, editForm.alt_phone, countryCodes, "Alt phone") && (
                <p className="text-[11px] text-rose-600">{phoneLengthError(editForm.country_code, editForm.alt_phone, countryCodes, "Alt phone")}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                     data-testid="edit-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Online order #</Label>
                <Input value={editForm.online_order_no} onChange={(e) => setEditForm({ ...editForm, online_order_no: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Store location</Label>
                <Input value={editForm.store_location} onChange={(e) => setEditForm({ ...editForm, store_location: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Client / Account</Label>
              <select
                value={editForm.client_id || ""}
                onChange={(e) => setEditForm({ ...editForm, client_id: e.target.value })}
                data-testid="edit-client-select"
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">— No client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <TagEditor value={editForm.tags || []} onChange={(v) => setEditForm({ ...editForm, tags: v })}
                         placeholder="Add a tag (e.g. vip)…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-save-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find duplicates dialog */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl" data-testid="dup-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-indigo-600" /> Possible duplicates
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Other customer records that share an email, a phone tail, or a very similar name with{" "}
              <b>{customer.name}</b>. Pick one to start a merge.
            </DialogDescription>
          </DialogHeader>
          {dupLoading && <div className="text-sm text-slate-400 py-6">Scanning…</div>}
          {!dupLoading && duplicates.length === 0 && (
            <div className="py-8 text-center" data-testid="dup-empty">
              <div className="text-3xl">🎯</div>
              <div className="text-sm font-medium text-slate-700 mt-1">No duplicates found</div>
              <p className="text-xs text-slate-500 mt-1">This customer record looks clean.</p>
            </div>
          )}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {duplicates.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-3 py-2 rounded border border-slate-200"
                   data-testid={`dup-candidate-${c.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-medium text-slate-800">{c.name || "(no name)"}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{c.phone_e164 || c.phone || "—"}</span>
                    {c.email && <><span>·</span><span>{c.email}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {c.ticket_count || 0} ticket{c.ticket_count === 1 ? "" : "s"}
                  </span>
                  {(c.match_reasons || []).map((r) => (
                    <span key={r} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
                      {r.replace("_", " ")}
                    </span>
                  ))}
                </div>
                <Button size="sm" onClick={() => setMergeTarget(c)}
                        className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs"
                        data-testid={`open-merge-${c.id}`}>
                  <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge into this
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MergeCustomerDialog
        open={!!mergeTarget}
        onOpenChange={(v) => { if (!v) setMergeTarget(null); }}
        primary={customer}
        secondary={mergeTarget}
        onMerged={() => {
          setMergeTarget(null);
          setDupOpen(false);
          load();
        }}
      />

      {/* GDPR delete confirmation */}
      <Dialog open={gdprOpen} onOpenChange={(v) => { if (!v) { setGdprOpen(false); setGdprConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="gdpr-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-rose-700">
              <Trash2 className="w-4 h-4" /> GDPR delete · permanent
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              This will <b>hard-delete</b> the customer record for <b>{customer.name}</b> and
              <b> anonymize</b> all {customer.stats?.ticket_count || 0} of their tickets
              (customer_name becomes "[Deleted Customer]"; phone/email/order # cleared).
              <span className="block mt-2 text-rose-700 font-semibold">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input value={gdprConfirm} onChange={(e) => setGdprConfirm(e.target.value)}
                   placeholder="DELETE" className="font-mono"
                   data-testid="gdpr-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGdprOpen(false); setGdprConfirm(""); }}>Cancel</Button>
            <Button onClick={gdprDelete} disabled={!gdprCanConfirm}
                    className="bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300"
                    data-testid="gdpr-confirm-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HealthChip({ health }) {
  const palette = {
    green: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
    amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
    red:   { bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200",   dot: "bg-rose-500" },
  }[health.band] || { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", dot: "bg-slate-400" };
  return (
    <Badge variant="outline"
           className={`${palette.bg} ${palette.text} ${palette.border} gap-1.5 px-2 py-0.5`}
           title={`Drivers: ${JSON.stringify(health.drivers)}`}
           data-testid="customer-health-chip">
      <span className={`w-2 h-2 rounded-full ${palette.dot}`} />
      Health {health.score} · {health.label}
    </Badge>
  );
}
